import {
  APPROVAL_LEVEL,
  APPROVAL_STATUS,
  ASSET_CONDITION,
  ASSET_STATUS,
  ASSET_TYPE,
  CHANGE_PRIORITY,
  CHANGE_RISK_LEVEL,
  CHANGE_TYPE,
  DEPLOYMENT_STATUS,
  IMPLEMENTATION_STATUS,
  INCIDENT_SEVERITY,
  MODEL_TYPE,
  MONITORING_REVIEW_STATUS,
  MONITORING_RISK_RATING,
  MONITORING_TYPE,
  REGULATION_TYPE,
  RESOLUTION_STATUS,
  REVIEW_FREQUENCY,
  SENSITIVE_DATA_CATEGORY,
  TRAINING_DATA_ORIGIN,
  formatEnumLabel,
} from '../constants/enums';
import {
  assetRegisterApi,
  businessProcessApi,
  businessUnitApi,
  incidentApi,
  listStatusParams,
  regulationApi,
  systemOwnerApi,
  vendorApi,
} from '../api/systemRegisterApi';
import {
  approvalViewPath,
  fetchApprovalDirectory,
} from '../entities/approval/approvalDirectory';
import { softwareToRequest } from '../utils/softwareMappers';
import CreateChangeManagementDialog from '../entities/change-management/createChangeManagementDialog';
import EditChangeManagementDialog from '../entities/change-management/editChangeManagementDialog';
import CreateIncidentDialog from '../entities/incident/createIncidentDialog';
import EditIncidentDialog from '../entities/incident/editIncidentDialog';
import {
  renderApprovalLevelChip,
  renderApprovalStatusChip,
  renderApprovalStatusEnumChip,
  renderChangeTypeChip,
  renderCurrentChip,
  renderDecisionChip,
  renderDeploymentStatusChip,
  renderDeploymentStatusLabelChip,
  renderEnumChip,
  renderImplementationStatusChip,
  renderMonitoringStatusChip,
  renderPriorityChip,
  renderResolutionStatusChip,
  renderRiskRatingChip,
} from '../shared/statusChips';

const lookupConfig = (entityName, api, fields, columns, toPayload) => ({
  mode: 'lookup',
  entityName,
  api: {
    list: (params) => api.list(params),
    create: (body) => api.create(body),
    update: (id, body) => api.update(id, body),
    remove: (id) => api.remove(id),
    restore: api.restore ? (id) => api.restore(id) : undefined,
  },
  fields,
  columns,
  toRow: (item) => ({ id: item.id, status: item.status || 'ACTIVE', ...item }),
  toPayload,
  getInitialValues: (row) => {
    const initial = {};
    fields.forEach((f) => {
      initial[f.name] = row?.[f.name] ?? (f.type === 'boolean' ? false : f.type === 'multiEnum' || f.type === 'multiLookupSelect' ? [] : '');
    });
    return initial;
  },
});

export const businessUnitConfig = lookupConfig(
  'Business Unit',
  businessUnitApi,
  [
    { name: 'name', label: 'Name', type: 'text', required: true },
    { name: 'description', label: 'Description', type: 'textarea' },
  ],
  [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 160 },
    { field: 'description', headerName: 'Description', flex: 1.5, minWidth: 200 },
  ],
  (values) => ({
    name: values.name?.trim(),
    description: values.description?.trim() || undefined,
  })
);

export const assetRegisterConfig = {
  ...lookupConfig(
  'IT Asset',
  assetRegisterApi,
  [
    { name: 'assetId', label: 'Asset ID', type: 'text' },
    { name: 'assetTag', label: 'Asset Tag', type: 'text' },
    { name: 'assetType', label: 'Asset Type', type: 'enum', options: ASSET_TYPE, gridSpan: 6 },
    { name: 'assetName', label: 'Asset Name', type: 'text', required: true },
    { name: 'manufacturer', label: 'Manufacturer', type: 'text' },
    { name: 'model', label: 'Model', type: 'text' },
    { name: 'serialNumber', label: 'Serial Number', type: 'text' },
    {
      name: 'assignedUserIds',
      label: 'Assigned To',
      type: 'multiLookupSelect',
      lookupKey: 'tenantUsers',
      optionLabel: 'label',
      fullWidth: true,
      hint: 'Select one or more tenant users',
    },
    { name: 'department', label: 'Department', type: 'text' },
    { name: 'location', label: 'Location', type: 'text' },
    { name: 'purchaseDate', label: 'Purchase Date', type: 'date' },
    { name: 'warrantyExpiry', label: 'Warranty Expiry', type: 'date' },
    { name: 'assetStatus', label: 'Asset Status', type: 'enum', options: ASSET_STATUS, gridSpan: 6 },
    { name: 'assetCondition', label: 'Asset Condition', type: 'enum', options: ASSET_CONDITION, gridSpan: 6 },
    { name: 'remarks', label: 'Remarks', type: 'textarea', fullWidth: true },
  ],
  [
    { field: 'assetId', headerName: 'Asset ID', width: 120 },
    { field: 'assetTag', headerName: 'Asset Tag', width: 130 },
    { field: 'assetType', headerName: 'Type', width: 120 },
    { field: 'assetName', headerName: 'Asset Name', flex: 1.2, minWidth: 160 },
    { field: 'manufacturer', headerName: 'Manufacturer', width: 120 },
    { field: 'model', headerName: 'Model', width: 130 },
    { field: 'serialNumber', headerName: 'Serial Number', width: 140 },
    {
      field: 'assignedTo',
      headerName: 'Assigned To',
      flex: 1,
      minWidth: 160,
      valueFormatter: (params) => params.value || '—',
    },
    { field: 'department', headerName: 'Department', width: 130 },
    { field: 'location', headerName: 'Location', width: 150 },
    { field: 'purchaseDate', headerName: 'Purchase Date', width: 130 },
    { field: 'warrantyExpiry', headerName: 'Warranty Expiry', width: 140 },
    {
      field: 'assetStatus',
      headerName: 'Status',
      width: 140,
      valueFormatter: (params) => formatEnumLabel(params.value) || '—',
    },
    {
      field: 'assetCondition',
      headerName: 'Condition',
      width: 120,
      valueFormatter: (params) => formatEnumLabel(params.value) || '—',
    },
    { field: 'remarks', headerName: 'Remarks', flex: 1, minWidth: 140 },
  ],
  (values) => ({
    assetId: values.assetId?.trim() || undefined,
    assetTag: values.assetTag?.trim() || undefined,
    assetType: values.assetType || undefined,
    assetName: values.assetName?.trim(),
    manufacturer: values.manufacturer?.trim() || undefined,
    model: values.model?.trim() || undefined,
    serialNumber: values.serialNumber?.trim() || undefined,
    assignedUserIds: Array.isArray(values.assignedUserIds)
      ? values.assignedUserIds.filter(Boolean)
      : [],
    department: values.department?.trim() || undefined,
    location: values.location?.trim() || undefined,
    purchaseDate: values.purchaseDate || undefined,
    warrantyExpiry: values.warrantyExpiry || undefined,
    assetStatus: values.assetStatus || 'IN_USE',
    assetCondition: values.assetCondition || undefined,
    remarks: values.remarks?.trim() || undefined,
  })
  ),
  formColumns: 2,
  loadTenantUsers: true,
  showView: true,
  getViewPath: (row) => `/system-register/it-asset-register/view?id=${row.id}`,
  toRow: (item) => ({
    id: item.id,
    status: item.status || 'ACTIVE',
    ...item,
    assignedUserIds: Array.isArray(item.assignedUserIds)
      ? item.assignedUserIds.map(String)
      : Array.isArray(item.assignedUsers)
        ? item.assignedUsers.map((u) => String(u.id))
        : [],
    assignedTo:
      item.assignedTo
      || (Array.isArray(item.assignedUsers)
        ? item.assignedUsers.map((u) => u.name || u.email).filter(Boolean).join(', ')
        : ''),
  }),
  getInitialValues: (row, fieldsList) => {
    const fields = fieldsList || [];
    const initial = {};
    fields.forEach((f) => {
      if (f.name === 'assignedUserIds') {
        initial.assignedUserIds = Array.isArray(row?.assignedUserIds)
          ? row.assignedUserIds.map(String)
          : [];
        return;
      }
      initial[f.name] =
        row?.[f.name]
        ?? (f.type === 'boolean' ? false : f.type === 'multiEnum' || f.type === 'multiLookupSelect' ? [] : '');
    });
    return initial;
  },
};

export const businessProcessConfig = lookupConfig(
  'Business Process',
  businessProcessApi,
  [
    { name: 'name', label: 'Name', type: 'text', required: true },
    { name: 'description', label: 'Description', type: 'textarea' },
  ],
  [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 160 },
    { field: 'description', headerName: 'Description', flex: 1.5, minWidth: 200 },
  ],
  (values) => ({
    name: values.name?.trim(),
    description: values.description?.trim() || undefined,
  })
);

export const systemOwnerConfig = lookupConfig(
  'System Owner',
  systemOwnerApi,
  [
    { name: 'fullName', label: 'Full name', type: 'text', required: true },
    { name: 'email', label: 'Email', type: 'text', required: true },
    { name: 'role', label: 'Role', type: 'text' },
    { name: 'jobTitle', label: 'Job title', type: 'text' },
  ],
  [
    { field: 'fullName', headerName: 'Full name', flex: 1, minWidth: 160 },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 180 },
    { field: 'role', headerName: 'Role', flex: 0.8, minWidth: 120 },
    { field: 'jobTitle', headerName: 'Job title', flex: 0.8, minWidth: 120 },
  ],
  (values) => ({
    fullName: values.fullName?.trim(),
    email: values.email?.trim(),
    role: values.role?.trim() || undefined,
    jobTitle: values.jobTitle?.trim() || undefined,
  })
);

export const vendorConfig = lookupConfig(
  'Vendor',
  vendorApi,
  [
    { name: 'vendorName', label: 'Vendor name', type: 'text', required: true },
    { name: 'contactEmail', label: 'Contact email', type: 'text' },
    { name: 'phoneNumber', label: 'Phone number', type: 'text' },
    { name: 'website', label: 'Website', type: 'text' },
  ],
  [
    { field: 'vendorName', headerName: 'Vendor', flex: 1, minWidth: 160 },
    { field: 'contactEmail', headerName: 'Email', flex: 1, minWidth: 180 },
    { field: 'phoneNumber', headerName: 'Phone number', flex: 1, minWidth: 140 },
    { field: 'website', headerName: 'Website', flex: 1, minWidth: 160 },
  ],
  (values) => ({
    vendorName: values.vendorName?.trim(),
    contactEmail: values.contactEmail?.trim() || undefined,
    phoneNumber: values.phoneNumber?.trim() || undefined,
    website: values.website?.trim() || undefined,
  })
);

export const regulationConfig = {
  ...lookupConfig(
    'Regulation',
    regulationApi,
    [
      {
        name: 'name',
        label: 'Regulation name',
        type: 'text',
        required: true,
        placeholder: 'e.g. GDPR, EU AI Act, Local privacy law',
      },
    ],
    [{ field: 'regulationName', headerName: 'Regulation', flex: 1, minWidth: 200 }],
    (values) => ({
      regulationName: values.name?.trim(),
    })
  ),
  toRow: (item) => ({
    id: item.id,
    name: item.regulationName,
    regulationName: item.regulationName,
    status: item.status || 'ACTIVE',
  }),
  getInitialValues: (row) => ({
    name: row?.regulationName ?? row?.name ?? '',
  }),
};

const nestedOneToOne = (entityName, nestedKey, fields, columns, mapNestedPayload) => ({
  mode: 'nested',
  entityName,
  nestedKey,
  fields: [
    {
      name: 'softwareId',
      label: 'Software',
      type: 'softwareSelect',
      required: true,
      hideOnEdit: true,
    },
    ...fields,
  ],
  columns: [
    { field: 'softwareName', headerName: 'Software', flex: 1, minWidth: 180 },
    ...columns,
  ],
  flatten: (systems) =>
    systems
      .filter((s) => s[nestedKey])
      .map((s) => ({
        id: s[nestedKey].id,
        softwareId: s.id,
        softwareName: s.softwareName,
        ...s[nestedKey],
      })),
  applyNested: (detail, values, isDelete) => {
    const request = softwareToRequest(detail);
    if (isDelete) {
      request[nestedKey] = null;
    } else {
      const payload = mapNestedPayload(values, detail);
      if (nestedKey === 'dataInformation' && detail?.dataInformation?.id) {
        payload.id = detail.dataInformation.id;
      }
      request[nestedKey] = payload;
    }
    return request;
  },
  toPayload: mapNestedPayload,
  getInitialValues: (row, fieldsList) => {
    const initial = { softwareId: row?.softwareId ?? '' };
    fieldsList.forEach((f) => {
      if (f.name === 'softwareId') return;
      initial[f.name] = row?.[f.name] ?? (f.type === 'boolean' ? false : f.type === 'multiEnum' ? [] : '');
    });
    return initial;
  },
});

const nestedCollectionConfig = (entityName, collectionKey, itemFields, itemColumns, mapItemPayload) => ({
  mode: 'nestedCollection',
  entityName,
  collectionPath: [collectionKey],
  fields: [
    { name: 'softwareId', label: 'Software', type: 'softwareSelect', required: true, hideOnEdit: true },
    ...itemFields,
  ],
  columns: [
    { field: 'softwareName', headerName: 'Software', flex: 1, minWidth: 180 },
    ...itemColumns,
  ],
  flatten: (systems) => {
    const rows = [];
    systems.forEach((s) => {
      (s[collectionKey] ?? []).forEach((item) => {
        rows.push({
          id: item.id,
          softwareId: s.id,
          softwareName: s.softwareName,
          ...item,
        });
      });
    });
    return rows;
  },
  toPayload: mapItemPayload,
  getInitialValues: (row, fieldsList) => {
    const initial = { softwareId: row?.softwareId ?? '' };
    fieldsList.forEach((f) => {
      if (f.name === 'softwareId') return;
      initial[f.name] = row?.[f.name] ?? (f.type === 'boolean' ? false : f.type === 'multiEnum' ? [] : '');
    });
    return initial;
  },
});

export const approvalConfig = {
  mode: 'standalone',
  entityName: 'Approval',
  supportsViewAll: true,
  supportsSoftDeleteList: true,
  lifecycleStatusField: 'recordStatus',
  listParams: (showAll) => listStatusParams(showAll),
  api: {
    list: (params) => fetchApprovalDirectory(params),
  },
  toRow: (item) => item,
  columns: [
    { field: 'procedureLabel', headerName: 'Procedure', flex: 1, minWidth: 160 },
    { field: 'subjectLabel', headerName: 'Related to', flex: 1.3, minWidth: 200 },
    {
      field: 'assetSummary',
      headerName: 'IT asset',
      flex: 1,
      minWidth: 140,
      valueFormatter: (params) => params.value && params.value !== '—' ? params.value : '—',
    },
    { field: 'approverName', headerName: 'Approver name', flex: 1, minWidth: 140 },
    { field: 'jobPosition', headerName: 'Job position', flex: 0.8, minWidth: 130 },
    { field: 'approverRole', headerName: 'Approver role', flex: 0.8, minWidth: 130 },
    { field: 'approvalDate', headerName: 'Approval date', width: 130 },
    { field: 'approvalStatus', headerName: 'Approval status', width: 150, renderCell: renderApprovalStatusEnumChip },
    { field: 'approvalLevel', headerName: 'Approval level', width: 140, renderCell: renderApprovalLevelChip },
    { field: 'approvalComments', headerName: 'Comments', flex: 1.2, minWidth: 180 },
    { field: 'signatureConfirmation', headerName: 'Signature', flex: 1, minWidth: 160 },
  ],
  hideCreate: true,
  hideEdit: true,
  showView: true,
  getViewPath: approvalViewPath,
};

export const deploymentStatusConfig = {
  ...nestedCollectionConfig(
    'Deployment Status',
    'deploymentStatuses',
    [
      {
        name: 'status',
        label: 'Deployment status',
        type: 'enum',
        options: DEPLOYMENT_STATUS,
        required: true,
      },
      { name: 'stageDate', label: 'Stage date', type: 'date' },
      { name: 'current', label: 'Current status for this system', type: 'boolean' },
    ],
    [
      { field: 'statusLabel', headerName: 'Status', flex: 1, minWidth: 140, renderCell: renderDeploymentStatusLabelChip },
      { field: 'stageDate', headerName: 'Stage date', width: 130 },
      {
        field: 'current',
        headerName: 'Current',
        width: 110,
        renderCell: renderCurrentChip,
      },
      { field: 'decisionStatusLabel', headerName: 'Decision', width: 130, renderCell: renderDecisionChip },
      {
        field: 'approvalsSummary',
        headerName: 'Approvals / comments',
        flex: 1.6,
        minWidth: 220,
      },
      {
        field: 'internalReference',
        headerName: 'Internal ID',
        width: 120,
      },
    ],
    (values) => ({
      status: values.status || undefined,
      stageDate: values.stageDate || undefined,
      current: Boolean(values.current),
      approvals: Array.isArray(values.approvals) ? values.approvals : undefined,
    })
  ),
  flatten: (systems) => {
    const rows = [];
    systems.forEach((s) => {
      (s.deploymentStatuses ?? []).forEach((item) => {
        const approvals = item.approvals ?? [];
        const decisionStatus = item.decisionStatus
          || (approvals.some((a) => a.decision === 'DECLINED') ? 'DECLINED' : approvals.length ? 'APPROVED' : '');
        rows.push({
          id: item.id,
          softwareId: s.id,
          softwareName: s.softwareName,
          internalReference: s.internalReference ?? '',
          status: item.status,
          statusLabel: formatEnumLabel(item.status) || item.status || '',
          stageDate: item.stageDate ?? '',
          current: Boolean(item.current),
          decisionStatus,
          decisionStatusLabel: decisionStatus ? formatEnumLabel(decisionStatus) : '—',
          approvals,
          approvalsSummary:
            approvals.length === 0
              ? 'None'
              : approvals
                  .map((a) => {
                    const who = a.name || a.email || 'Approver';
                    const decision = a.decision === 'DECLINED' ? 'Declined' : a.decision === 'APPROVED' ? 'Approved' : '';
                    const when = a.approvedAt
                      ? String(a.approvedAt).replace('T', ' ').slice(0, 16)
                      : '';
                    const comment = a.comment ? ` “${a.comment}”` : '';
                    return [decision, who, when].filter(Boolean).join(' · ') + comment;
                  })
                  .join('; '),
        });
      });
    });
    return rows;
  },
  getInitialValues: (row) => ({
    softwareId: row?.softwareId ?? '',
    status: row?.status ?? '',
    stageDate: row?.stageDate ?? '',
    current: Boolean(row?.current),
    approvals: row?.approvals ?? [],
  }),
  hideCreate: true,
  hideEdit: true,
  showView: true,
  getViewPath: (row) =>
    `/system-register/deployment-statuses/view?softwareId=${row.softwareId}&id=${row.id}`,
};

function formatIncidentDateTime(value) {
  if (value == null || value === '') return '—';
  const raw = String(value);
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return raw.includes('T') ? raw.replace('T', ' ').slice(0, 16) : raw;
}

function latestIncidentApprovedAt(approvals = []) {
  const times = approvals
    .filter((a) => a?.approvalStatus === 'APPROVED')
    .map((a) => a.approvedAt || a.approvalDate)
    .filter(Boolean)
    .map((v) => String(v))
    .sort();
  return times[times.length - 1] || '';
}

const incidentNestedPayload = (values) => ({
  incidentResponseOwner: values.incidentResponseOwner?.trim() || undefined,
  knownIncidents: Boolean(values.knownIncidents),
  incidentDetails: values.incidentDetails?.trim() || undefined,
  escalationPathDefined: Boolean(values.escalationPathDefined),
  incidentDate: values.incidentDate || undefined,
  severityLevel: values.severityLevel || values.riskPriority || undefined,
  rootCause: values.rootCause?.trim() || undefined,
  resolutionStatus: values.resolutionStatus || 'OPEN',
  actionTaken: values.actionTaken?.trim() || undefined,
  reportedBy: values.reportedBy?.trim() || undefined,
  incidentReportingStaff: values.incidentReportingStaff?.trim() || undefined,
  riskRegisterIds: Array.isArray(values.riskRegisterIds)
    ? values.riskRegisterIds.filter((id) => id != null)
    : values.riskRegisterId != null
      ? [values.riskRegisterId]
      : [],
  assetRegisterIds: Array.isArray(values.assetRegisterIds)
    ? values.assetRegisterIds.filter((id) => id != null)
    : [],
});

export const incidentConfig = {
  mode: 'standalone',
  entityName: 'Incident',
  api: incidentApi,
  supportsViewAll: true,
  supportsSoftDeleteList: true,
  lifecycleStatusField: 'recordStatus',
  listParams: (showAll) => listStatusParams(showAll),
  collectionPath: ['incidents'],
  updateViaNested: true,
  toNestedPayload: incidentNestedPayload,
  toPayload: (values) => ({
    softwareId: values.targetType === 'IT_ASSET' ? null : (values.softwareId ?? null),
    incident: incidentNestedPayload(values),
  }),
  toRow: (item) => {
    const approvals = item.approvals ?? [];
    const hasDeclined = approvals.some(
      (a) => a.approvalStatus === 'REJECTED' || a.approvalStatus === 'DECLINED'
    );
    const hasApproved = approvals.some((a) => a.approvalStatus === 'APPROVED');
    const approvalStatus = hasDeclined
      ? 'Declined'
      : hasApproved
        ? 'Approved'
        : approvals.length
          ? formatEnumLabel(approvals[0].approvalStatus) || 'Pending'
          : 'None';
    const assetNames = (item.assets || [])
      .map((a) => a.assetName || a.assetId)
      .filter(Boolean)
      .join(', ');
    return {
      ...item,
      id: item.id,
      softwareId: item.softwareId ?? null,
      softwareName: item.softwareName || (assetNames ? '—' : ''),
      assetNames: assetNames || '—',
      targetLabel: item.softwareName
        ? item.softwareName
        : assetNames || '—',
      incidentTicket: item.incidentTicket ?? '',
      incidentDetails: item.incidentDetails ?? '',
      riskRating: item.severityLevel ?? item.riskPriority ?? '',
      status: item.resolutionStatus ?? '',
      incidentResponseOwner: item.incidentResponseOwner ?? '',
      reportedBy: item.reportedBy || item.incidentReportingStaff || '',
      reportedAt: item.reportedAt || item.incidentDate || '',
      solvedApprovedAt: item.solvedApprovedAt || latestIncidentApprovedAt(approvals),
      recordStatus: item.recordStatus || 'ACTIVE',
      approvalStatus,
      assetRegisterIds: item.assetRegisterIds ?? [],
      assets: item.assets ?? [],
    };
  },
  columns: [
    {
      field: 'incidentTicket',
      headerName: 'Incident Ticket',
      width: 160,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'targetLabel',
      headerName: 'Software / IT Asset',
      flex: 1.1,
      minWidth: 180,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'incidentDetails',
      headerName: 'Incident Description',
      flex: 1.2,
      minWidth: 180,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'reportedBy',
      headerName: 'Reported by',
      flex: 0.9,
      minWidth: 150,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'incidentResponseOwner',
      headerName: 'Assigned to',
      flex: 0.9,
      minWidth: 150,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'reportedAt',
      headerName: 'Reported at',
      width: 180,
      valueFormatter: (params) => formatIncidentDateTime(params.value),
    },
    {
      field: 'solvedApprovedAt',
      headerName: 'Approved as solved',
      width: 180,
      valueFormatter: (params) => formatIncidentDateTime(params.value),
    },
    {
      field: 'riskRating',
      headerName: 'Risk rating',
      width: 140,
      renderCell: renderRiskRatingChip,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: renderResolutionStatusChip,
    },
    {
      field: 'approvalStatus',
      headerName: 'Approval status',
      width: 160,
      renderCell: renderApprovalStatusChip,
    },
  ],
  fields: [],
  hideCreate: false,
  hideEdit: false,
  showView: true,
  CreateDialog: CreateIncidentDialog,
  EditDialog: EditIncidentDialog,
  getViewPath: (row) =>
    row.softwareId
      ? `/system-register/incidents/view?softwareId=${row.softwareId}&id=${row.id}`
      : `/system-register/incidents/view?id=${row.id}`,
};

export const changeManagementConfig = {
  ...nestedCollectionConfig(
    'Change Management',
    'changeManagements',
    [
      { name: 'title', label: 'Title', type: 'text', required: true },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'changeType', label: 'Change type', type: 'enum', options: CHANGE_TYPE },
      { name: 'priority', label: 'Priority', type: 'enum', options: CHANGE_PRIORITY },
      { name: 'riskLevel', label: 'Risk level', type: 'enum', options: CHANGE_RISK_LEVEL },
      { name: 'reasonForChange', label: 'Reason for change', type: 'textarea' },
      { name: 'requestedBy', label: 'Requested by', type: 'text' },
      { name: 'requestedDate', label: 'Requested date', type: 'datetime' },
      { name: 'currentVersion', label: 'Current version', type: 'text' },
      { name: 'targetVersion', label: 'Target version', type: 'text' },
      { name: 'implementationPlan', label: 'Implementation plan', type: 'textarea' },
      { name: 'rollbackPlan', label: 'Rollback plan', type: 'textarea' },
      { name: 'testPlan', label: 'Test plan', type: 'textarea' },
      { name: 'testResult', label: 'Test result', type: 'textarea' },
      { name: 'scheduledStart', label: 'Scheduled start', type: 'datetime' },
      { name: 'scheduledEnd', label: 'Scheduled end', type: 'datetime' },
      { name: 'actualStart', label: 'Actual start', type: 'datetime' },
      { name: 'actualEnd', label: 'Actual end', type: 'datetime' },
      { name: 'downtimeRequired', label: 'Downtime required', type: 'boolean' },
      { name: 'downtimeDuration', label: 'Downtime duration (minutes)', type: 'number' },
      {
        name: 'implementationStatus',
        label: 'Implementation status',
        type: 'enum',
        options: IMPLEMENTATION_STATUS,
      },
      { name: 'implementedBy', label: 'Implemented by', type: 'text' },
    ],
    [
      { field: 'title', headerName: 'Title', flex: 1.2, minWidth: 160 },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1.2,
        minWidth: 180,
        valueFormatter: (params) => params.value || '—',
      },
      { field: 'changeType', headerName: 'Change type', width: 140, renderCell: renderChangeTypeChip },
      { field: 'priority', headerName: 'Priority', width: 120, renderCell: renderPriorityChip },
      {
        field: 'riskLevel',
        headerName: 'Risk level',
        width: 130,
        valueFormatter: (params) => formatEnumLabel(params.value) || '—',
      },
      {
        field: 'reasonForChange',
        headerName: 'Reason for change',
        flex: 1.2,
        minWidth: 180,
        valueFormatter: (params) => params.value || '—',
      },
      {
        field: 'requestedBy',
        headerName: 'Requested by',
        flex: 1,
        minWidth: 160,
        valueFormatter: (params) => params.value || '—',
      },
      {
        field: 'requestedDate',
        headerName: 'Requested date',
        width: 170,
        valueFormatter: (params) => params.value || '—',
      },
      {
        field: 'currentVersion',
        headerName: 'Current version',
        width: 140,
        valueFormatter: (params) => params.value || '—',
      },
      {
        field: 'targetVersion',
        headerName: 'Target version',
        width: 140,
        valueFormatter: (params) => params.value || '—',
      },
      {
        field: 'implementationPlan',
        headerName: 'Implementation plan',
        flex: 1.2,
        minWidth: 180,
        valueFormatter: (params) => params.value || '—',
      },
      {
        field: 'rollbackPlan',
        headerName: 'Rollback plan',
        flex: 1.2,
        minWidth: 180,
        valueFormatter: (params) => params.value || '—',
      },
      {
        field: 'testPlan',
        headerName: 'Test plan',
        flex: 1.2,
        minWidth: 160,
        valueFormatter: (params) => params.value || '—',
      },
      {
        field: 'testResult',
        headerName: 'Test result',
        flex: 1.2,
        minWidth: 160,
        valueFormatter: (params) => params.value || '—',
      },
      {
        field: 'scheduledStart',
        headerName: 'Scheduled start',
        width: 170,
        valueFormatter: (params) => params.value || '—',
      },
      {
        field: 'scheduledEnd',
        headerName: 'Scheduled end',
        width: 170,
        valueFormatter: (params) => params.value || '—',
      },
      {
        field: 'actualStart',
        headerName: 'Actual start',
        width: 170,
        valueFormatter: (params) => params.value || '—',
      },
      {
        field: 'actualEnd',
        headerName: 'Actual end',
        width: 170,
        valueFormatter: (params) => params.value || '—',
      },
      {
        field: 'downtimeRequired',
        headerName: 'Downtime required',
        width: 150,
        valueFormatter: (params) => (params.value ? 'Yes' : 'No'),
      },
      {
        field: 'downtimeDuration',
        headerName: 'Downtime duration (minutes)',
        width: 190,
        valueFormatter: (params) =>
          params.value === '' || params.value == null ? '—' : String(params.value),
      },
      {
        field: 'implementationStatus',
        headerName: 'Implementation status',
        width: 170,
        renderCell: renderImplementationStatusChip,
      },
      {
        field: 'implementedBy',
        headerName: 'Implemented by',
        flex: 1,
        minWidth: 150,
        valueFormatter: (params) => params.value || '—',
      },
    ],
    (values) => ({
      title: values.title?.trim(),
      description: values.description?.trim() || undefined,
      changeType: values.changeType || undefined,
      priority: values.priority || undefined,
      riskLevel: values.riskLevel || undefined,
      reasonForChange: values.reasonForChange?.trim() || undefined,
      requestedBy: values.requestedBy?.trim() || undefined,
      requestedDate: values.requestedDate || undefined,
      currentVersion: values.currentVersion?.trim() || undefined,
      targetVersion: values.targetVersion?.trim() || undefined,
      implementationPlan: values.implementationPlan?.trim() || undefined,
      rollbackPlan: values.rollbackPlan?.trim() || undefined,
      testPlan: values.testPlan?.trim() || undefined,
      testResult: values.testResult?.trim() || undefined,
      scheduledStart: values.scheduledStart || undefined,
      scheduledEnd: values.scheduledEnd || undefined,
      actualStart: values.actualStart || undefined,
      actualEnd: values.actualEnd || undefined,
      downtimeRequired: Boolean(values.downtimeRequired),
      downtimeDuration:
        values.downtimeDuration === '' || values.downtimeDuration == null
          ? undefined
          : Number(values.downtimeDuration),
      implementationStatus: values.implementationStatus || undefined,
      implementedBy: values.implementedBy?.trim() || undefined,
    })
  ),
  hideCreate: false,
  hideEdit: false,
  canEdit: (row) => {
    const status = String(row?.implementationStatus || '').toUpperCase();
    if (status === 'COMPLETED') return false;
    const approvals = row?.approvals ?? [];
    const firstApproved = approvals.some(
      (a) => a?.approverRole === '1st Approval' && a?.approvalStatus === 'APPROVED'
    );
    const secondApproved = approvals.some(
      (a) => a?.approverRole === '2nd Approval' && a?.approvalStatus === 'APPROVED'
    );
    return !(firstApproved && secondApproved);
  },
  showView: true,
  CreateDialog: CreateChangeManagementDialog,
  EditDialog: EditChangeManagementDialog,
  getViewPath: (row) =>
    `/system-register/change-managements/view?softwareId=${row.softwareId}&id=${row.id}`,
};

export const reviewConfig = {
  ...nestedCollectionConfig(
    'Review',
    'monitoringReviews',
    [
      { name: 'reviewAreaControl', label: 'Review Area / Control', type: 'text' },
      { name: 'riskRating', label: 'Risk Rating', type: 'enum', options: MONITORING_RISK_RATING },
      { name: 'status', label: 'Status', type: 'enum', options: MONITORING_REVIEW_STATUS },
      { name: 'nextReviewDate', label: 'Next review', type: 'date' },
      { name: 'monitoringType', label: 'Monitoring type', type: 'enum', options: MONITORING_TYPE },
      { name: 'reviewFrequency', label: 'Review frequency', type: 'enum', options: REVIEW_FREQUENCY },
      { name: 'observationFindings', label: 'Observation / findings', type: 'textarea' },
      { name: 'correctiveActionRequired', label: 'Corrective action required', type: 'textarea' },
      { name: 'actionOwnerEmail', label: 'Action owner', type: 'text' },
      { name: 'reviewer', label: 'Reviewer', type: 'text' },
    ],
    [],
    (values) => ({
      reviewAreaControl: values.reviewAreaControl?.trim() || undefined,
      riskRating: values.riskRating || undefined,
      status: values.status || undefined,
      nextReviewDate: values.nextReviewDate || undefined,
      monitoringType: values.monitoringType || undefined,
      reviewFrequency: values.reviewFrequency || undefined,
      observationFindings: values.observationFindings?.trim() || undefined,
      correctiveActionRequired: values.correctiveActionRequired?.trim() || undefined,
      actionOwnerEmail: values.actionOwnerEmail?.trim() || undefined,
      reviewer: values.reviewer?.trim() || undefined,
    })
  ),
  columns: [
    {
      field: 'softwareName',
      headerName: 'Software name',
      flex: 1.1,
      minWidth: 160,
    },
    {
      field: 'reviewAreaControl',
      headerName: 'Review Area / Control',
      flex: 1.3,
      minWidth: 180,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'riskRating',
      headerName: 'Risk Rating',
      width: 140,
      renderCell: renderRiskRatingChip,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: renderMonitoringStatusChip,
    },
    {
      field: 'nextReviewDate',
      headerName: 'Next review',
      width: 130,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'approvalStatus',
      headerName: 'Approval status',
      width: 160,
      renderCell: renderApprovalStatusChip,
    },
  ],
  flatten: (systems) => {
    const rows = [];
    systems.forEach((s) => {
      (s.monitoringReviews ?? []).forEach((item) => {
        const approvals = item.approvals ?? [];
        const hasDeclined = approvals.some(
          (a) => a.approvalStatus === 'REJECTED' || a.approvalStatus === 'DECLINED'
        );
        const hasApproved = approvals.some((a) => a.approvalStatus === 'APPROVED');
        const approvalStatus = hasDeclined
          ? 'Declined'
          : hasApproved
            ? 'Approved'
            : approvals.length
              ? formatEnumLabel(approvals[0].approvalStatus) || 'Pending'
              : 'None';

        rows.push({
          ...item,
          id: item.id,
          softwareId: s.id,
          softwareName: s.softwareName,
          reviewAreaControl: item.reviewAreaControl ?? '',
          riskRating: item.riskRating ?? '',
          status: item.status ?? '',
          nextReviewDate: item.nextReviewDate ?? '',
          approvalStatus,
        });
      });
    });
    return rows;
  },
  hideCreate: true,
  hideEdit: true,
  showView: true,
  getViewPath: (row) =>
    `/system-register/reviews/view?softwareId=${row.softwareId}&id=${row.id}`,
};

export const riskAssessmentConfig = {
  ...nestedOneToOne(
    'Risk Assessment',
    'riskAssessment',
    [
      { name: 'knownBias', label: 'Known bias', type: 'boolean' },
      { name: 'biasDescription', label: 'Bias description', type: 'textarea' },
      { name: 'usesArtificialIntelligence', label: 'Uses artificial intelligence', type: 'boolean' },
    ],
    [
      { field: 'knownBias', headerName: 'Known bias', width: 110, type: 'boolean' },
      { field: 'usesArtificialIntelligence', headerName: 'Uses AI', width: 100, type: 'boolean' },
    ],
    (values) => ({
      knownBias: Boolean(values.knownBias),
      biasDescription: values.biasDescription?.trim() || undefined,
      usesArtificialIntelligence: Boolean(values.usesArtificialIntelligence),
    })
  ),
  hideCreate: true,
  hideEdit: true,
  showView: true,
  getViewPath: (row) =>
    `/system-register/risk-assessments/view?softwareId=${row.softwareId}`,
};

export const dataInformationConfig = {
  ...nestedOneToOne(
    'System Artificial Intelligence Details',
    'dataInformation',
    [
      { name: 'personalData', label: 'Personal data', type: 'boolean' },
      { name: 'trainingDataOrigin', label: 'Training data origin', type: 'enum', options: TRAINING_DATA_ORIGIN },
      { name: 'modelType', label: 'Model type', type: 'enum', options: MODEL_TYPE },
      {
        name: 'sensitiveDataCategories',
        label: 'Sensitive categories',
        type: 'multiEnum',
        options: SENSITIVE_DATA_CATEGORY,
      },
    ],
    [
      {
        field: 'usesArtificialIntelligence',
        headerName: 'Uses AI',
        width: 100,
        type: 'boolean',
      },
      { field: 'personalData', headerName: 'Personal data', width: 120, type: 'boolean' },
      {
        field: 'dataSourcesUsed',
        headerName: 'Data sources used',
        flex: 1.2,
        minWidth: 180,
      },
      {
        field: 'sensitiveDataCategoriesLabel',
        headerName: 'Sensitive categories',
        flex: 1,
        minWidth: 160,
      },
      { field: 'trainingDataOrigin', headerName: 'Training origin', flex: 1, minWidth: 140, renderCell: renderEnumChip },
      { field: 'modelType', headerName: 'Model type', flex: 1, minWidth: 140, renderCell: renderEnumChip },
      { field: 'knownBias', headerName: 'Known bias', width: 110, type: 'boolean' },
      {
        field: 'biasDescription',
        headerName: 'Bias description',
        flex: 1.2,
        minWidth: 180,
      },
    ],
    (values) => ({
      personalData: Boolean(values.personalData),
      trainingDataOrigin: values.trainingDataOrigin || undefined,
      modelType: values.modelType || undefined,
      sensitiveDataCategories: values.sensitiveDataCategories ?? [],
    })
  ),
  flatten: (systems) =>
    (systems ?? []).map((s) => {
      const di = s.dataInformation;
      const risk = s.riskAssessment;
      const sources = s.dataSources ?? [];
      const categories = di?.sensitiveDataCategories ?? [];
      const usesAi =
        risk?.usesArtificialIntelligence != null
          ? Boolean(risk.usesArtificialIntelligence)
          : Boolean(
              sources.length
                || di?.personalData
                || categories.length > 0
                || di?.modelType
                || di?.trainingDataOrigin
            );
      return {
        id: di?.id ?? `software-${s.id}`,
        softwareId: s.id,
        softwareName: s.softwareName,
        usesArtificialIntelligence: usesAi,
        dataSourcesUsed: sources.map((ds) => ds.sourceName).filter(Boolean).join(', ') || '—',
        personalData: Boolean(di?.personalData),
        sensitiveDataCategories: categories,
        sensitiveDataCategoriesLabel: categories.length
          ? categories.map((c) => formatEnumLabel(c) || c).join(', ')
          : '—',
        modelType: di?.modelType || '',
        trainingDataOrigin: di?.trainingDataOrigin || '',
        knownBias: Boolean(risk?.knownBias),
        biasDescription: risk?.biasDescription || '—',
      };
    }),
  hideCreate: true,
  hideEdit: true,
  showView: true,
  getViewPath: (row) =>
    `/system-register/data-information/view?softwareId=${row.softwareId}`,
};

export const dataSourceConfig = {
  mode: 'nestedCollection',
  entityName: 'Data Source',
  collectionPath: ['dataSources'],
  fields: [
    { name: 'softwareId', label: 'Software', type: 'softwareSelect', required: true, hideOnEdit: true },
    { name: 'sourceName', label: 'Source name', type: 'text', required: true },
    { name: 'description', label: 'Description', type: 'textarea' },
  ],
  columns: [
    { field: 'softwareName', headerName: 'Software', flex: 1, minWidth: 160 },
    { field: 'sourceName', headerName: 'Source name', flex: 1, minWidth: 160 },
    { field: 'description', headerName: 'Description', flex: 1.5, minWidth: 180 },
  ],
  flatten: (systems) => {
    const rows = [];
    systems.forEach((s) => {
      (s.dataSources ?? []).forEach((ds) => {
        rows.push({
          id: ds.id,
          softwareId: s.id,
          softwareName: s.softwareName,
          sourceName: ds.sourceName,
          description: ds.description,
        });
      });
    });
    return rows;
  },
  toPayload: (values) => ({
    sourceName: values.sourceName?.trim(),
    description: values.description?.trim() || undefined,
  }),
  getInitialValues: (row) => ({
    softwareId: row?.softwareId ?? '',
    sourceName: row?.sourceName ?? '',
    description: row?.description ?? '',
  }),
  hideCreate: true,
  hideEdit: true,
  showView: true,
  getViewPath: (row) =>
    `/system-register/data-sources/view?softwareId=${row.softwareId}`,
};

export const documentConfig = {
  mode: 'nestedCollection',
  entityName: 'Document',
  collectionPath: ['documents'],
  fields: [
    { name: 'softwareId', label: 'Software', type: 'softwareSelect', required: true, hideOnEdit: true },
    { name: 'documentName', label: 'Document name', type: 'text', required: true },
    { name: 'deploymentStatus', label: 'Deployment status', type: 'enum', options: DEPLOYMENT_STATUS, required: true },
    { name: 'fileName', label: 'Attached file name', type: 'text' },
    { name: 'location', label: 'File location / URL', type: 'text' },
  ],
  columns: [
    { field: 'softwareName', headerName: 'Software', flex: 1, minWidth: 160 },
    { field: 'documentName', headerName: 'Document name', flex: 1, minWidth: 160 },
    { field: 'deploymentStatus', headerName: 'Deployment status', width: 160, renderCell: renderDeploymentStatusChip },
    { field: 'fileName', headerName: 'File', flex: 1, minWidth: 160 },
    { field: 'location', headerName: 'Location', flex: 1.2, minWidth: 180 },
  ],
  flatten: (systems) => {
    const rows = [];
    systems.forEach((s) => {
      (s.documents ?? []).forEach((doc) => {
        rows.push({
          id: doc.id,
          softwareId: s.id,
          softwareName: s.softwareName,
          documentName: doc.documentName,
          deploymentStatus: doc.deploymentStatus,
          fileName: doc.fileName,
          location: doc.location,
        });
      });
    });
    return rows;
  },
  toPayload: (values) => ({
    documentName: values.documentName?.trim(),
    deploymentStatus: values.deploymentStatus || undefined,
    fileName: values.fileName?.trim(),
    location: values.location?.trim(),
  }),
  getInitialValues: (row) => ({
    softwareId: row?.softwareId ?? '',
    documentName: row?.documentName ?? '',
    deploymentStatus: row?.deploymentStatus ?? '',
    fileName: row?.fileName ?? '',
    location: row?.location ?? '',
  }),
  hideCreate: true,
  hideEdit: true,
  showView: true,
  getViewPath: (row) =>
    `/system-register/documents/view?softwareId=${row.softwareId}&id=${row.id}`,
};

export const ENTITY_NAV_ITEMS = [
  {
    key: 'software',
    label: 'Software',
    path: '/system-register/software',
    description: 'Register and manage software in your organization.',
  },
  {
    key: 'business-unit',
    label: 'Business Units',
    path: '/system-register/business-units',
    description: 'Define business units linked to software.',
  },
  {
    key: 'business-process',
    label: 'Business Processes',
    path: '/system-register/business-processes',
    description: 'Catalog business processes used in use-case descriptions.',
  },
  {
    key: 'system-owner',
    label: 'System Owners',
    path: '/system-register/system-owners',
    description: 'Track owners responsible for each software.',
  },
  {
    key: 'vendor',
    label: 'Vendors',
    path: '/system-register/vendors',
    description: 'Manage third-party vendors and providers.',
  },
  {
    key: 'regulation',
    label: 'Regulations',
    path: '/system-register/regulations',
    description: 'Reference regulations applicable to AI use.',
  },
  {
    key: 'data-information',
    label: 'System Artificial Intelligence Details',
    path: '/system-register/data-information',
    description: 'View artificial intelligence details for each software system.',
  },
  {
    key: 'data-source',
    label: 'Data Sources',
    path: '/system-register/data-sources',
    description: 'Record sources feeding AI models.',
  },
  {
    key: 'risk-register',
    label: 'Risk Register',
    path: '/system-register/risk-register',
    description: 'Record, assess, and track risks across registered systems.',
  },
  {
    key: 'document',
    label: 'Documents',
    path: '/system-register/documents',
    description: 'Store policies and supporting documents.',
  },
  {
    key: 'review',
    label: 'Reviews',
    path: '/system-register/reviews',
    description: 'Maintain the monitoring review catalog (type, area, frequency).',
  },
  {
    key: 'incident',
    label: 'Incidents',
    path: '/system-register/incidents',
    description: 'Log and review AI incidents.',
  },
  {
    key: 'access-provision',
    label: 'Access Administration',
    path: '/system-register/access-provisions',
    description: 'Request new access, modifications, or removal for users.',
  },
  {
    key: 'change-management',
    label: 'Change Management',
    path: '/system-register/change-managements',
    description: 'Plan and track system changes across software.',
  },
  {
    key: 'approval',
    label: 'Approvals',
    path: '/system-register/approvals',
    description: 'Capture approval workflows and sign-offs.',
  },
  {
    key: 'deployment-status',
    label: 'Deployment Status',
    path: '/system-register/deployment-statuses',
    description: 'View all registered deployment status stages across software systems.',
  },
];

export const SOFTWARE_FORM_FIELDS = {
  coreOnly: [
    {
      name: 'softwareName',
      label: 'Software name',
      type: 'text',
      required: true,
      placeholder: 'e.g. Customer retention assistant',
      hint: 'The official name used in the software register.',
    },
    {
      name: 'internalReference',
      label: 'Internal ID',
      type: 'text',
      readOnly: true,
      placeholder: 'Auto-generated (S-001…)',
    },
    {
      name: 'externalOrganisationReference',
      label: 'Optional organisation reference ID',
      type: 'text',
      placeholder: 'e.g. CRM-AI-RETENTION',
    },
    {
      name: 'contractStartDate',
      label: 'Contract start date',
      type: 'date',
      gridSpan: 3,
      sx: { '& .MuiInputBase-root': { height: 56 } },
    },
    {
      name: 'contractEndDate',
      label: 'Contract end date',
      type: 'date',
      gridSpan: 3,
      sx: { '& .MuiInputBase-root': { height: 56 } },
    },
    {
      name: 'contractDuration',
      label: 'Contract duration',
      type: 'number',
      min: 0,
      gridSpan: 3,
      placeholder: 'Auto from dates',
      sx: { '& .MuiInputBase-root': { height: 56 } },
    },
    {
      name: 'contractDurationUnit',
      label: 'Contract unit',
      type: 'enum',
      options: ['DAYS'],
      gridSpan: 3,
      labelAbove: false,
      inputSize: 'medium',
      selectSx: {
        minWidth: '100%',
        width: '100%',
        height: 56,
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          minHeight: '1.4375em',
          boxSizing: 'border-box',
        },
      },
    },
    {
      name: 'licenseStartDate',
      label: 'License start date',
      type: 'date',
      gridSpan: 3,
      sx: { '& .MuiInputBase-root': { height: 56 } },
    },
    {
      name: 'licenseEndDate',
      label: 'License end date',
      type: 'date',
      gridSpan: 3,
      sx: { '& .MuiInputBase-root': { height: 56 } },
    },
    {
      name: 'licenseDuration',
      label: 'License duration',
      type: 'number',
      min: 0,
      gridSpan: 3,
      placeholder: 'Auto from dates',
      sx: { '& .MuiInputBase-root': { height: 56 } },
    },
    {
      name: 'licenseDurationUnit',
      label: 'License unit',
      type: 'enum',
      options: ['DAYS'],
      gridSpan: 3,
      labelAbove: false,
      inputSize: 'medium',
      selectSx: {
        minWidth: '100%',
        width: '100%',
        height: 56,
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          minHeight: '1.4375em',
          boxSizing: 'border-box',
        },
      },
    },
    {
      name: 'primaryPurpose',
      label: 'Primary purpose',
      type: 'textarea',
      placeholder: 'Describe the main objective and intended outcomes…',
      hint: 'What this software is intended to achieve.',
      fullWidth: true,
    },
  ],
  core: [
    { name: 'softwareName', label: 'Software name', type: 'text', required: true },
    {
      name: 'internalReference',
      label: 'Internal ID',
      type: 'text',
      readOnly: true,
      placeholder: 'Auto-generated (S-001…)',
    },
    {
      name: 'externalOrganisationReference',
      label: 'Optional organisation reference ID',
      type: 'text',
      placeholder: 'e.g. CRM-AI-RETENTION',
    },
    {
      name: 'contractStartDate',
      label: 'Contract start date',
      type: 'date',
      gridSpan: 3,
      sx: { '& .MuiInputBase-root': { height: 56 } },
    },
    {
      name: 'contractEndDate',
      label: 'Contract end date',
      type: 'date',
      gridSpan: 3,
      sx: { '& .MuiInputBase-root': { height: 56 } },
    },
    {
      name: 'contractDuration',
      label: 'Contract duration',
      type: 'number',
      min: 0,
      gridSpan: 3,
      placeholder: 'Auto from dates',
      sx: { '& .MuiInputBase-root': { height: 56 } },
    },
    {
      name: 'contractDurationUnit',
      label: 'Contract unit',
      type: 'enum',
      options: ['DAYS'],
      gridSpan: 3,
      labelAbove: false,
      inputSize: 'medium',
      selectSx: {
        minWidth: '100%',
        width: '100%',
        height: 56,
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          minHeight: '1.4375em',
          boxSizing: 'border-box',
        },
      },
    },
    {
      name: 'licenseStartDate',
      label: 'License start date',
      type: 'date',
      gridSpan: 3,
      sx: { '& .MuiInputBase-root': { height: 56 } },
    },
    {
      name: 'licenseEndDate',
      label: 'License end date',
      type: 'date',
      gridSpan: 3,
      sx: { '& .MuiInputBase-root': { height: 56 } },
    },
    {
      name: 'licenseDuration',
      label: 'License duration',
      type: 'number',
      min: 0,
      gridSpan: 3,
      placeholder: 'Auto from dates',
      sx: { '& .MuiInputBase-root': { height: 56 } },
    },
    {
      name: 'licenseDurationUnit',
      label: 'License unit',
      type: 'enum',
      options: ['DAYS'],
      gridSpan: 3,
      labelAbove: false,
      inputSize: 'medium',
      selectSx: {
        minWidth: '100%',
        width: '100%',
        height: 56,
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          minHeight: '1.4375em',
          boxSizing: 'border-box',
        },
      },
    },
    { name: 'primaryPurpose', label: 'Primary purpose', type: 'textarea' },
    {
      name: 'businessUnitIds',
      label: 'Business units',
      type: 'multiLookupSelect',
      lookupKey: 'businessUnits',
      optionLabel: 'name',
      required: true,
    },
    {
      name: 'systemOwnerIds',
      label: 'System owners',
      type: 'multiLookupSelect',
      lookupKey: 'systemOwners',
      optionLabel: 'fullName',
      required: true,
    },
    {
      name: 'vendorIds',
      label: 'Vendors',
      type: 'multiLookupSelect',
      lookupKey: 'vendors',
      optionLabel: 'vendorName',
    },
    {
      name: 'regulationTypes',
      label: 'Regulations',
      type: 'multiEnum',
      options: REGULATION_TYPE,
    },
  ],
};
